import { fromJson } from '@client/core/data/concerns/parses-from-json';
import { defineTransform } from '@client/core/data/concerns/parses-from-json/transform-map';
import { Nullable } from '@client/core/data/types';
import { ConstructionMaterialCategory } from '@client/features/material-catalog/models/construction-material-category';
import { ConstructionMaterialSupplier } from '@client/features/material-catalog/models/construction-material-supplier';

export class ConstructionMaterial {
  constructor(
    public readonly id: string,
    public readonly supplier: Nullable<ConstructionMaterialSupplier>,
    public readonly category: Nullable<ConstructionMaterialCategory>,
    public readonly description: string,
    public readonly price: Nullable<number>,
    public readonly unit: Nullable<string>,
    public readonly url: Nullable<string>,
    public readonly itemSku: Nullable<string>,
    public readonly internetSku: Nullable<string>,
    public readonly notes: Nullable<string>,
    public readonly imageUrl: Nullable<string>,
  ) {}

  /**
   * Determine if the given material is an instance of a ConstructionMaterial or EditableConstructionMaterial
   */
  public static isConstructionMaterial(
    material: ConstructionMaterial | EditableConstructionMaterial,
  ): material is ConstructionMaterial {
    return 'supplier' in material;
  }
}

defineTransform(ConstructionMaterial, (json, { nullOr }) => {
  return {
    supplier: nullOr(json.supplier, () => fromJson(ConstructionMaterialSupplier, json.supplier)),
    category: nullOr(json.category, () => ConstructionMaterialCategory.findBySlug(json.category)),
    itemSku: json.item_sku,
    internetSku: json.internet_sku,
    imageUrl: json.image_url,
  };
});

/**
 * The typing which represents the an editable version of the Construction Material.
 */
export interface EditableConstructionMaterial {
  id?: string;
  supplierId: Nullable<string>;
  supplierName: Nullable<string>;
  category: Nullable<string>;
  description: string;
  price: Nullable<number>;
  unit: Nullable<string>;
  url: Nullable<string>;
  itemSku: Nullable<string>;
  internetSku: Nullable<string>;
  notes: Nullable<string>;
  imageUrl: Nullable<string>;
}
